/**
 * Générateur — Utiliser des proportions, des pourcentages.
 * Attendu ID : 981 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 981.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Utiliser des proportions, des pourcentages. »
 *
 * Catégorie « automatismes » (niveau 1 : 1 %, 10 %, 50 % d'un nombre entier rond, de tête)
 *   - automatismes_qcm_unique      QCM : p % de N, p tiré parmi {1, 10, 50}
 *   - automatismes_texte_trou      Trous (saisie) : 1 %, 10 % et 50 % de trois nombres ronds,
 *                                  les trois valeurs du programme servies à CHAQUE tirage
 *   - automatismes_tableau_vf      V/F : « 1 % de 300, c'est 3 » (vrai / faux par confusion 1 % ↔ 10 %)
 *
 * Catégorie « pourcentages » (niveaux 2 et 3 : prendre un pourcentage, remise, augmentation)
 *   - pourcentage_nombre_texte     Saisie : p % (multiple de 5) d'un effectif entier, réponse entière
 *   - remise_montant_texte         Saisie (niveau 2, question 1) : montant de la remise, en euros
 *   - remise_prix_texte            Saisie (niveau 2, question 2) : prix après remise, en euros
 *   - prix_final_texte             Saisie (niveau 3) : prix décimal, augmentation ou diminution,
 *                                  prix final demandé directement
 *   - prix_final_qcm_unique        QCM (niveaux 2-3) : prix après remise / après augmentation,
 *                                  distracteurs = erreurs visées
 *   - remise_tableau_vf            V/F : montant et prix final d'une remise ou d'une augmentation
 *
 * Catégorie « echelle » (niveau 4 : échelle d'un plan, conversion d'unité, unité exigée)
 *   - echelle_trouver_texte        Saisie : l'échelle à partir des deux longueurs (fraction)
 *   - echelle_longueur_reelle_texte Saisie : longueur réelle, réponse avec unité (m ou km)
 *   - echelle_longueur_plan_texte  Saisie : longueur sur le plan, réponse avec unité (cm)
 *   - echelle_longueur_reelle_qcm_unique QCM : longueur réelle, distracteurs = erreurs visées
 *   - echelle_relier               Relier : échelle ↔ « 1 cm sur le plan représente … »
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - pourcentage_automatise : {1, 10, 50} (prescrit) ; le trou sert les trois à chaque tirage.
 *   - pourcentage_courant : multiples de 5 dans [5, 90] (décidé) ; la carte « p % d'un effectif »
 *     couvre toute la plage ; les remises commerciales sont tirées dans [5, 50] et les
 *     augmentations dans [5, 30] (point de vigilance : « une remise de 80 % décrédibilise »).
 *   - nombre_sur_lequel_le_pourcentage_s_applique : [20, 600], entier aux niveaux 1-2,
 *     décimal à deux décimales au niveau 3 (montant exact au centime par construction).
 *   - sens_d_utilisation_du_pourcentage : prendre / diminuer / augmenter (les trois servis).
 *   - coefficient_multiplicateur_equivalent : jamais demandé, jamais nommé (exclusion).
 *   - echelle_d_un_plan : {100, 200, 500, 1000, 2000, 5000, 25000, 50000, 100000, 500000}.
 *   - longueur_sur_le_plan : [1,5 ; 45] cm, un chiffre après la virgule, jamais 10, 20, 30, 40.
 *   - unite_de_la_reponse : euro, cm, m, km ; l'unité est tapée avec le nombre, la consigne
 *     l'annonce, toute écriture exacte de la même grandeur est admise (liste finie,
 *     contenu.reponses_acceptees, comparaison = 'expression').
 * Exclusions respectées : aucun rapport ni ratio, aucun coefficient multiplicateur, aucune
 *   échelle d'agrandissement, aucun taux > 100 %, aucune longueur ronde en dizaines de cm,
 *   aucun arrondi sous le centime.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. taux et coefficient confondus (30 % du prix rendu pour le prix après remise)
 *        → `taux_pris_pour_coefficient`
 *   2. augmentation : multiplier par le taux sans ajouter au nombre de départ
 *        → `augmentation_reduite_au_montant`
 *   3. 1 % pris pour un dixième (confusion 1 % ↔ 10 %)
 *        → `un_pour_cent_pris_pour_un_dixieme`
 *   4. conversion d'unité oubliée dans un calcul d'échelle
 *        → `conversion_oubliee`
 *   5. diviser par l'échelle au lieu de multiplier, ou l'inverse
 *        → `sens_de_l_echelle_inverse`
 *   6. nombre rendu sans unité pour une grandeur
 *        → `nombre_sans_unite`
 *   Compléments (hors spec, familles de distracteurs supplémentaires) :
 *        `taux_pris_pour_moitie`, `moitie_prise_pour_un_dixieme`, `virgule_decalee`,
 *        `taux_soustrait_au_prix`, `sens_de_l_operation_inverse`.
 *
 * Graduation → cartes :
 *   niveau 1 → automatismes_qcm_unique, automatismes_texte_trou, automatismes_tableau_vf
 *   niveau 2 → pourcentage_nombre_texte, remise_montant_texte, remise_prix_texte,
 *              prix_final_qcm_unique (prix entier), remise_tableau_vf
 *   niveau 3 → prix_final_texte, prix_final_qcm_unique (prix décimal)
 *   niveau 4 → echelle_trouver_texte, echelle_longueur_reelle_texte,
 *              echelle_longueur_plan_texte, echelle_longueur_reelle_qcm_unique, echelle_relier
 *
 * Points non tranchés (voir audit/5eme/981.md) :
 *   - « sans élargir ni rétrécir » les bornes contre « une remise de 80 % décrédibilise » :
 *     remise bornée à 50 %, augmentation à 30 %, la plage complète [5, 90] reste servie par
 *     la carte « p % d'un effectif ».
 *   - « Les montants sont attendus au centime » : « 12,5 € » est admis à côté de « 12,50 € ».
 *   - La correction à deux méthodes (point de vigilance 8) n'a pas de canal dans le payload.
 *
 * Invariants : bonne réponse unique par construction (arithmétique entière en centimes et
 * en dixièmes de cm), boucles bornées avec repli déterministe, distracteurs distincts de la
 * bonne réponse ou nouveau tirage complet.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 981;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Mots-clés des erreurs visées (spec 981.json) ───
export const ERREURS_VISEES = {
    taux_pris_pour_coefficient: 'Confondre le taux et le coefficient : rendre 30 % du prix pour le prix après une remise de 30 %',
    augmentation_reduite_au_montant: 'Appliquer une augmentation en multipliant par le taux sans ajouter le montant au nombre de départ',
    un_pour_cent_pris_pour_un_dixieme: 'Prendre 1 % pour un dixième du nombre (confusion 1 % ↔ 10 %)',
    conversion_oubliee: 'Oublier la conversion d\'unité dans un calcul d\'échelle',
    sens_de_l_echelle_inverse: 'Diviser par l\'échelle au lieu de multiplier, ou l\'inverse',
    nombre_sans_unite: 'Rendre un nombre sans unité quand la question porte sur une grandeur',
};

export const REGLES_DISTRACTEURS_QCM = {
    ...ERREURS_VISEES,
    taux_pris_pour_moitie: 'Prendre la moitié au lieu de p % (complément)',
    moitie_prise_pour_un_dixieme: 'Prendre un dixième au lieu de la moitié (complément)',
    virgule_decalee: 'Résultat dix fois trop grand ou trop petit (complément)',
    taux_soustrait_au_prix: 'Soustraire le taux au prix comme s\'il était en euros (complément)',
    sens_de_l_operation_inverse: 'Ajouter la remise ou retrancher l\'augmentation (complément)',
};

// ═══════════════════════════════════════════════════════════
// HELPERS NUMÉRIQUES (arithmétique entière, affichage français)
// ═══════════════════════════════════════════════════════════

const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

/** Entier de centimes → texte français (« 1250 » → « 12,50 », « 8400 » → « 84 »). */
const centimes = (c) => {
    const e = Math.floor(c / 100);
    const r = c % 100;
    return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0')}`;
};

/** Entier de centimes → texte à deux décimales imposées (« 8400 » → « 84,00 »). */
const centimes2 = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;

/** Entier n divisé par 10^k → écriture décimale exacte, sans zéro inutile. */
const divPuissance10 = (n, k) => {
    if (k === 0) return String(n);
    const s = String(n).padStart(k + 1, '0');
    const ent = s.slice(0, s.length - k);
    const dec = s.slice(s.length - k).replace(/0+$/, '');
    return dec ? `${ent},${dec}` : ent;
};

/** Dixièmes de cm → texte (« 45 » → « 4,5 », « 120 » → « 12 »). */
const dixiemes = (d) => divPuissance10(d, 1);

// ─── Écritures admises d'une grandeur (liste finie, comparaison = 'expression') ───

const NOMS_UNITES = {
    '€': ['€', 'euro', 'euros'],
    cm: ['cm', 'centimètre', 'centimètres', 'centimetre', 'centimetres'],
    mm: ['mm', 'millimètre', 'millimètres', 'millimetre', 'millimetres'],
    m: ['m', 'mètre', 'mètres', 'metre', 'metres'],
    km: ['km', 'kilomètre', 'kilomètres', 'kilometre', 'kilometres'],
};

/** Toutes les écritures « nombre unité » d'une liste de couples [texteNombre, unite]. */
const ecrituresGrandeur = (couples) => {
    const out = [];
    for (const [nombre, unite] of couples) {
        for (const u of NOMS_UNITES[unite]) {
            for (const e of [`${nombre} ${u}`, `${nombre}${u}`]) {
                if (!out.includes(e)) out.push(e);
            }
        }
    }
    return out;
};

/** Écritures admises d'un montant en centimes : « 12,50 € », « 12,5 € », « 84 € », « 84,00 € ». */
const ecrituresMontant = (c) => {
    const nombres = new Set([centimes(c), centimes2(c)]);
    if (c % 10 === 0 && c % 100 !== 0) nombres.add(divPuissance10(c / 10, 1));
    return ecrituresGrandeur([...nombres].map((n) => [n, '€']));
};

/**
 * Écritures admises d'une longueur donnée en dixièmes de mm (entier), dans les
 * unités mm, cm, m, km : toute écriture exacte est admise, la comparaison porte
 * sur la GRANDEUR (point de vigilance 3 de la spec).
 */
const ecrituresLongueur = (dixiemesDeMm) => {
    const couples = [
        [divPuissance10(dixiemesDeMm, 1), 'mm'],
        [divPuissance10(dixiemesDeMm, 2), 'cm'],
        [divPuissance10(dixiemesDeMm, 4), 'm'],
        [divPuissance10(dixiemesDeMm, 7), 'km'],
    ];
    // Variante avec un zéro final pour les décimaux (« 4,50 cm ») et « 90,0 m ».
    const avecZero = couples.map(([n, u]) => [n.includes(',') ? `${n}0` : `${n},0`, u]);
    return ecrituresGrandeur([...couples, ...avecZero]);
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 : AUTOMATISMES (1 %, 10 %, 50 %)
// ═══════════════════════════════════════════════════════════

const TAUX_AUTOMATISES = [1, 10, 50];

/** Nombre entier rond dont p % se prend de tête avec un résultat entier ou en demi. */
const nombreRondPour = (p) => {
    if (p === 1) return 50 * randInt(2, 12);          // 100..600 par pas de 50 → 1 % = entier ou x,5
    if (p === 10) return 10 * randInt(2, 60);          // 20..600 par pas de 10
    return 20 * randInt(1, 30);                        // 20..600 par pas de 20 → 50 % = dizaine entière
};

/** p % de N en centièmes d'unité (entier exact). */
const pourcentCentiemes = (p, N) => p * N;

const texteCentiemes = (c) => divPuissance10(c, 2);

const genAutomatismesQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const p = randomChoice(TAUX_AUTOMATISES);
        const N = nombreRondPour(p);
        const bonC = pourcentCentiemes(p, N);
        const candidats = [];
        if (p === 1) {
            candidats.push({ code: 'un_pour_cent_pris_pour_un_dixieme', c: N * 10 });   // N/10
            candidats.push({ code: 'taux_pris_pour_moitie', c: N * 50 });               // N/2
            candidats.push({ code: 'virgule_decalee', c: N / 10 });                     // N/1000
        } else if (p === 10) {
            candidats.push({ code: 'un_pour_cent_pris_pour_un_dixieme', c: N });        // N/100
            candidats.push({ code: 'taux_pris_pour_moitie', c: N * 50 });               // N/2
            candidats.push({ code: 'virgule_decalee', c: N * 100 });                    // N
        } else {
            candidats.push({ code: 'moitie_prise_pour_un_dixieme', c: N * 10 });        // N/10
            candidats.push({ code: 'un_pour_cent_pris_pour_un_dixieme', c: N });        // N/100
            candidats.push({ code: 'virgule_decalee', c: N * 500 });                    // 5N
        }
        const valides = candidats.filter((d) => Number.isInteger(d.c) && d.c > 0 && d.c !== bonC);
        const textes = new Set(valides.map((d) => texteCentiemes(d.c)));
        if (valides.length < 3 || textes.size < 3 || textes.has(texteCentiemes(bonC))) continue;

        const bonne = texteCentiemes(bonC);
        const payload = makeQcm(
            [bonne, ...valides.map((d) => texteCentiemes(d.c))],
            bonne,
            `<p>Calcule de tête : ${p} % de ${N}.</p>`,
            opts('Prendre 1 %, 10 % ou 50 % d\'un nombre'),
        );
        payload._meta.params = {
            niveau: 1, taux: p, nombre: N, reponse: bonne,
            distracteurs: valides.map((d) => ({ code: d.code, valeur: texteCentiemes(d.c) })),
        };
        return payload;
    }
    // Jamais atteint (les trois distracteurs sont distincts par construction) ; repli : 10 % de 300.
    const payload = makeQcm(['30', '3', '150', '300'], '30', '<p>Calcule de tête : 10 % de 300.</p>', opts('Prendre 1 %, 10 % ou 50 % d\'un nombre'));
    payload._meta.params = {
        niveau: 1, taux: 10, nombre: 300, reponse: '30',
        distracteurs: [{ code: 'un_pour_cent_pris_pour_un_dixieme', valeur: '3' }, { code: 'taux_pris_pour_moitie', valeur: '150' }, { code: 'virgule_decalee', valeur: '300' }],
    };
    return payload;
};

const genAutomatismesTrou = () => {
    // Les trois taux du programme, chacun sur son nombre, dans un ordre mélangé.
    const lignes = shuffle(TAUX_AUTOMATISES).map((p) => {
        const N = nombreRondPour(p);
        return { p, N, rep: texteCentiemes(pourcentCentiemes(p, N)) };
    });
    const phrase = lignes.map((l) => `${l.p} % de ${l.N}, c'est {{${l.rep}}}`).join('\n');
    const payload = makeTexteTrou(
        phrase,
        '<p>Calcule de tête et complète chaque ligne.</p>',
        { ...opts('Calculer 1 %, 10 % et 50 % de tête'), mode: 'saisie' },
    );
    payload._meta.params = {
        niveau: 1,
        lignes: lignes.map((l) => ({ taux: l.p, nombre: l.N, reponse: l.rep })),
        erreursVisees: ['un_pour_cent_pris_pour_un_dixieme'],
    };
    return payload;
};

const genAutomatismesVF = () => {
    // 4 lignes, deux vraies et deux fausses dans un ordre mélangé. Faux = confusion
    // 1 % ↔ 10 % (erreur visée 3), moitié ↔ dixième ou taux ↔ moitié (compléments).
    const plan = shuffle([true, true, false, false]);
    const lignes = [];
    const bonnes = [];
    const codes = [];
    const vus = new Set();
    for (const vrai of plan) {
        let placee = false;
        for (let essai = 0; essai < 20 && !placee; essai++) {
            const p = randomChoice(TAUX_AUTOMATISES);
            const N = nombreRondPour(p);
            let c;
            let code = null;
            if (vrai) {
                c = pourcentCentiemes(p, N);
            } else if (p === 1) {
                c = N * 10; code = 'un_pour_cent_pris_pour_un_dixieme';
            } else if (p === 10) {
                const k = randInt(0, 1);
                c = k === 0 ? N : N * 50;
                code = k === 0 ? 'un_pour_cent_pris_pour_un_dixieme' : 'taux_pris_pour_moitie';
            } else {
                c = N * 10; code = 'moitie_prise_pour_un_dixieme';
            }
            const texte = `${p} % de ${N}, c'est ${texteCentiemes(c)}`;
            if (vus.has(texte)) continue;
            vus.add(texte);
            lignes.push(texte);
            bonnes.push(vrai ? 0 : 1);
            codes.push(code);
            placee = true;
        }
    }
    if (lignes.length < 4) {
        // Repli déterministe : deux vraies, deux fausses.
        return makeVFDeterministe();
    }
    const payload = makeVraiFaux(
        lignes, bonnes,
        '<p>Pour chaque ligne, indique si le calcul est vrai ou faux.</p>',
        opts('Vrai/Faux : 1 %, 10 %, 50 % d\'un nombre'),
    );
    payload._meta.params = {
        niveau: 1,
        distracteurs: codes.filter(Boolean).map((code) => ({ code })),
    };
    return payload;
};

const makeVFDeterministe = () => {
    const lignes = ['1 % de 300, c\'est 3', '10 % de 250, c\'est 25', '1 % de 400, c\'est 40', '50 % de 80, c\'est 8'];
    const bonnes = [0, 0, 1, 1];
    const payload = makeVraiFaux(
        lignes, bonnes,
        '<p>Pour chaque ligne, indique si le calcul est vrai ou faux.</p>',
        opts('Vrai/Faux : 1 %, 10 %, 50 % d\'un nombre'),
    );
    payload._meta.params = {
        niveau: 1,
        distracteurs: [{ code: 'un_pour_cent_pris_pour_un_dixieme' }, { code: 'moitie_prise_pour_un_dixieme' }],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 : POURCENTAGES (prendre, diminuer, augmenter)
// ═══════════════════════════════════════════════════════════

const TAUX_COURANTS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90];
const TAUX_REMISE = TAUX_COURANTS.filter((t) => t <= 50);
const TAUX_AUGMENTATION = TAUX_COURANTS.filter((t) => t <= 30);

const EFFECTIFS = [
    { groupe: 'un collège', personnes: 'élèves', part: 'sont demi-pensionnaires' },
    { groupe: 'un collège', personnes: 'élèves', part: 'viennent à pied' },
    { groupe: 'un club de sport', personnes: 'licenciés', part: 'sont des filles' },
    { groupe: 'une chorale', personnes: 'choristes', part: 'sont des adultes' },
    { groupe: 'un cinéma', personnes: 'spectateurs', part: 'ont moins de 14 ans' },
    { groupe: 'une bibliothèque', personnes: 'livres', part: 'sont des bandes dessinées' },
    { groupe: 'un parking', personnes: 'voitures', part: 'sont électriques' },
    { groupe: 'un verger', personnes: 'arbres', part: 'sont des pommiers' },
];

/** Articles soldés et fourchette de prix vraisemblable (en euros, dans [20, 600]). */
const ARTICLES = [
    { nom: 'un vélo', min: 150, max: 600 },
    { nom: 'une paire de baskets', min: 40, max: 180 },
    { nom: 'un jeu vidéo', min: 20, max: 80 },
    { nom: 'un casque audio', min: 30, max: 300 },
    { nom: 'une veste', min: 40, max: 200 },
    { nom: 'un sac à dos', min: 20, max: 120 },
    { nom: 'une tablette', min: 100, max: 600 },
    { nom: 'une trottinette', min: 100, max: 600 },
    { nom: 'un ballon de basket', min: 20, max: 60 },
    { nom: 'une montre', min: 30, max: 400 },
];

/** Situations d'augmentation et fourchette de prix vraisemblable (en euros, dans [20, 600]). */
const AUGMENTATIONS = [
    { objet: 'Le prix d\'un abonnement mensuel à une salle de sport', min: 20, max: 80 },
    { objet: 'Le loyer mensuel d\'un studio', min: 300, max: 600 },
    { objet: 'Le prix d\'un billet de concert', min: 20, max: 150 },
    { objet: 'Le prix d\'un forfait de ski à la journée', min: 30, max: 80 },
    { objet: 'Le prix d\'un abonnement annuel à un magazine', min: 40, max: 150 },
];

/** « de » + mot, avec l'élision devant une voyelle (« d'élèves », « de livres »). */
const de = (mot) => (/^[aeiouyéèêàh]/i.test(mot) ? `d'${mot}` : `de ${mot}`);

/** Effectif entier N dans [20, 600] tel que p % de N soit entier. */
const effectifPour = (p) => {
    const g = 100 / gcd(p, 100);
    return g * randInt(Math.ceil(20 / g), Math.floor(600 / g));
};

const genPourcentageNombre = () => {
    const p = randomChoice(TAUX_COURANTS);
    const N = effectifPour(p);
    const rep = (p * N) / 100;
    const ctx = randomChoice(EFFECTIFS);
    const payload = makeSaisie(
        String(rep),
        `<p>Dans ${ctx.groupe}, il y a ${N} ${ctx.personnes} et ${p} % d'entre eux ${ctx.part}.</p>`
        + `<p>Combien ${de(ctx.personnes)} ${ctx.part} ?</p>`,
        opts('Prendre un pourcentage d\'un effectif'),
    );
    payload._meta.params = { niveau: 2, taux: p, nombre: N, reponse: String(rep) };
    return payload;
};

/** Prix décimal (deux décimales, non entier) en centimes dans [min, max] € tel que t % du prix soit exact au centime. */
const prixDecimalPour = (t, min, max) => {
    const g = 100 / gcd(t, 100);
    for (let essai = 0; essai < 60; essai++) {
        const c = g * randInt(Math.ceil((min * 100) / g), Math.floor((max * 100) / g));
        if (c % 100 !== 0) return c;
    }
    // Repli : min + 0,20 € est multiple de 20, 10, 5, 4 et 2 centimes → exact pour tout taux multiple de 5.
    return min * 100 + 20;
};

/**
 * Tirage d'une situation commerciale : sens (diminuer / augmenter), contexte et sa fourchette,
 * taux, prix P (entier, ou décimal non entier si `decimal`), montant M et prix final F, en centimes.
 */
const tirerSituation = (sens, decimal) => {
    const ctx = sens === 'diminuer' ? randomChoice(ARTICLES) : randomChoice(AUGMENTATIONS);
    const t = sens === 'diminuer' ? randomChoice(TAUX_REMISE) : randomChoice(TAUX_AUGMENTATION);
    const P = decimal ? prixDecimalPour(t, ctx.min, ctx.max) : randInt(ctx.min, ctx.max) * 100;
    const M = (P * t) / 100;
    const F = sens === 'diminuer' ? P - M : P + M;
    return { sens, ctx, t, P, M, F };
};

/** Tirage d'une situation « prix final », sens tiré (niveau 2 si entier, niveau 3 si décimal). */
const tirerPrixFinal = (decimal) => tirerSituation(randomChoice(['diminuer', 'augmenter']), decimal);

const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const enonceRemise = (article, P, t) => `<p>${majuscule(article.nom)} coûte ${centimes(P)} €. Pendant les soldes, ce prix est diminué de ${t} %.</p>`;

const enonceAugmentation = (ctx, P, t) => `<p>${ctx.objet} était de ${centimes(P)} €. Il augmente de ${t} %.</p>`;

const enonceSituation = (s) => (s.sens === 'diminuer' ? enonceRemise(s.ctx, s.P, s.t) : enonceAugmentation(s.ctx, s.P, s.t));

const consigneEuros = '<p>Écris la réponse avec son unité, par exemple : 12,50 €.</p>';

const genRemiseMontant = () => {
    const s = tirerSituation('diminuer', false);
    const payload = makeSaisie(
        `${centimes(s.M)} €`,
        `${enonceRemise(s.ctx, s.P, s.t)}<p>Quel est le montant de la remise ?</p>${consigneEuros}`,
        { ...opts('Calculer le montant d\'une remise'), comparaison: 'expression', reponses_acceptees: ecrituresMontant(s.M) },
    );
    payload._meta.params = { niveau: 2, taux: s.t, prix: centimes(s.P), reponse: centimes(s.M), erreursVisees: ['nombre_sans_unite'] };
    return payload;
};

const genRemisePrix = () => {
    const s = tirerSituation('diminuer', false);
    const payload = makeSaisie(
        `${centimes(s.F)} €`,
        `${enonceRemise(s.ctx, s.P, s.t)}<p>Quel est le prix après la remise ?</p>${consigneEuros}`,
        { ...opts('Calculer le prix après une remise'), comparaison: 'expression', reponses_acceptees: ecrituresMontant(s.F) },
    );
    payload._meta.params = {
        niveau: 2, taux: s.t, prix: centimes(s.P), montant: centimes(s.M), reponse: centimes(s.F),
        erreursVisees: ['taux_pris_pour_coefficient', 'nombre_sans_unite'],
    };
    return payload;
};

const enoncePrixFinal = (s) => `${enonceSituation(s)}<p>Quel est le nouveau prix ?</p>`;

const genPrixFinalSaisie = () => {
    const s = tirerPrixFinal(true);
    const payload = makeSaisie(
        `${centimes(s.F)} €`,
        `${enoncePrixFinal(s)}${consigneEuros}`,
        { ...opts('Calculer un prix après augmentation ou diminution'), comparaison: 'expression', reponses_acceptees: ecrituresMontant(s.F) },
    );
    payload._meta.params = {
        niveau: 3, sens: s.sens, taux: s.t, prix: centimes(s.P), montant: centimes(s.M), reponse: centimes(s.F),
        erreursVisees: [s.sens === 'diminuer' ? 'taux_pris_pour_coefficient' : 'augmentation_reduite_au_montant', 'nombre_sans_unite'],
    };
    return payload;
};

const genPrixFinalQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const decimal = randInt(0, 1) === 1;
        const s = tirerPrixFinal(decimal);
        const bonne = `${centimes(s.F)} €`;
        const candidats = [
            // Le montant seul, rendu comme prix final (erreurs visées 1 et 2 selon le sens).
            { code: s.sens === 'diminuer' ? 'taux_pris_pour_coefficient' : 'augmentation_reduite_au_montant', c: s.M },
            // Le taux soustrait ou ajouté comme s'il était en euros.
            { code: 'taux_soustrait_au_prix', c: s.sens === 'diminuer' ? s.P - s.t * 100 : s.P + s.t * 100 },
            // Le sens de l'opération inversé.
            { code: 'sens_de_l_operation_inverse', c: s.sens === 'diminuer' ? s.P + s.M : s.P - s.M },
        ].filter((d) => d.c > 0 && d.c !== s.F);
        const textes = new Set(candidats.map((d) => centimes(d.c)));
        if (candidats.length < 3 || textes.size < 3 || textes.has(centimes(s.F))) continue;

        const payload = makeQcm(
            [bonne, ...candidats.map((d) => `${centimes(d.c)} €`)],
            bonne,
            enoncePrixFinal(s),
            opts('QCM : prix après remise ou augmentation'),
        );
        payload._meta.params = {
            niveau: decimal ? 3 : 2, sens: s.sens, taux: s.t, prix: centimes(s.P), montant: centimes(s.M), reponse: centimes(s.F),
            distracteurs: candidats.map((d) => ({ code: d.code, valeur: `${centimes(d.c)} €` })),
        };
        return payload;
    }
    // Repli déterministe : 240 € diminué de 30 % → 168 € ; 72, 210 et 312 sont distincts.
    const payload = makeQcm(
        ['168 €', '72 €', '210 €', '312 €'], '168 €',
        `${enonceRemise(ARTICLES[0], 24000, 30)}<p>Quel est le nouveau prix ?</p>`,
        opts('QCM : prix après remise ou augmentation'),
    );
    payload._meta.params = {
        niveau: 2, sens: 'diminuer', taux: 30, prix: '240', montant: '72', reponse: '168',
        distracteurs: [{ code: 'taux_pris_pour_coefficient', valeur: '72 €' }, { code: 'taux_soustrait_au_prix', valeur: '210 €' }, { code: 'sens_de_l_operation_inverse', valeur: '312 €' }],
    };
    return payload;
};

const genRemiseVF = () => {
    for (let essai = 0; essai < 40; essai++) {
        const s = tirerPrixFinal(false);
        const brut = s.sens === 'diminuer' ? s.P - s.t * 100 : s.P + s.t * 100;
        if (brut <= 0) continue;
        const mot = s.sens === 'diminuer' ? 'la remise' : 'l\'augmentation';
        const propositions = [
            { texte: `Le montant de ${mot} est de ${centimes(s.M)} €.`, vrai: true, code: null },
            { texte: `Le nouveau prix est de ${centimes(s.F)} €.`, vrai: true, code: null },
            { texte: `Le nouveau prix est de ${centimes(s.M)} €.`, vrai: false, code: s.sens === 'diminuer' ? 'taux_pris_pour_coefficient' : 'augmentation_reduite_au_montant' },
            { texte: `Le nouveau prix est de ${centimes(brut)} €.`, vrai: false, code: 'taux_soustrait_au_prix' },
            { texte: `Le montant de ${mot} est de ${centimes(s.F)} €.`, vrai: false, code: 'sens_de_l_operation_inverse' },
        ];
        // Deux vraies, deux fausses tirées parmi les trois, dans un ordre mélangé.
        const fausses = shuffle(propositions.filter((p) => !p.vrai)).slice(0, 2);
        const retenues = shuffle([...propositions.filter((p) => p.vrai), ...fausses]);
        const textes = new Set(retenues.map((p) => p.texte));
        if (textes.size < 4) continue;
        const valeurs = new Set([s.M, s.F, brut]);
        if (valeurs.size < 3) continue;

        const enonce = enonceSituation(s);
        const payload = makeVraiFaux(
            retenues.map((p) => p.texte),
            retenues.map((p) => (p.vrai ? 0 : 1)),
            `${enonce}<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`,
            opts('Vrai/Faux : montant et nouveau prix'),
        );
        payload._meta.params = {
            niveau: 2, sens: s.sens, taux: s.t, prix: centimes(s.P), montant: centimes(s.M), reponse: centimes(s.F),
            distracteurs: fausses.map((p) => ({ code: p.code })),
        };
        return payload;
    }
    // Repli déterministe : 240 € diminué de 30 %.
    const payload = makeVraiFaux(
        ['Le montant de la remise est de 72 €.', 'Le nouveau prix est de 72 €.', 'Le nouveau prix est de 168 €.', 'Le nouveau prix est de 210 €.'],
        [0, 1, 0, 1],
        `${enonceRemise(ARTICLES[0], 24000, 30)}<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`,
        opts('Vrai/Faux : montant et nouveau prix'),
    );
    payload._meta.params = {
        niveau: 2, sens: 'diminuer', taux: 30, prix: '240', montant: '72', reponse: '168',
        distracteurs: [{ code: 'taux_pris_pour_coefficient' }, { code: 'taux_soustrait_au_prix' }],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 3 : ÉCHELLE D'UN PLAN
// ═══════════════════════════════════════════════════════════

/** Échelles d'usage (spec : dénominateurs d'une fraction de numérateur 1). */
const ECHELLES = [
    { E: 100, doc: 'le plan d\'une maison', objets: ['un couloir', 'un salon', 'une terrasse', 'un garage'], lMax: 120 },
    { E: 200, doc: 'le plan d\'une maison', objets: ['un couloir', 'un jardin', 'une terrasse', 'une clôture'], lMax: 120 },
    { E: 500, doc: 'le plan d\'un quartier', objets: ['une rue', 'une allée', 'un terrain de sport', 'un parking'], lMax: 450 },
    { E: 1000, doc: 'le plan d\'un quartier', objets: ['une rue', 'une avenue', 'un parc', 'une piste cyclable'], lMax: 450 },
    { E: 2000, doc: 'le plan d\'un quartier', objets: ['une rue', 'une avenue', 'un parc', 'une piste cyclable'], lMax: 450 },
    { E: 5000, doc: 'le plan d\'une ville', objets: ['une avenue', 'un boulevard', 'une ligne de tramway', 'une piste cyclable'], lMax: 450 },
    { E: 25000, doc: 'une carte de randonnée', objets: ['un sentier', 'un chemin', 'un lac', 'une piste forestière'], lMax: 450 },
    { E: 50000, doc: 'une carte de randonnée', objets: ['un sentier', 'un chemin', 'une vallée', 'une piste forestière'], lMax: 450 },
    { E: 100000, doc: 'une carte routière', objets: ['une route', 'une autoroute', 'une voie ferrée', 'un canal'], lMax: 450 },
    { E: 500000, doc: 'une carte routière', objets: ['une route', 'une autoroute', 'une voie ferrée', 'un fleuve'], lMax: 450 },
];

/** Longueur réelle en dixièmes de mm → { valeur (texte), unite } courte (m ou km, un chiffre décimal au plus). */
const grandeurCourte = (dmm) => {
    if (dmm >= 10000000 && dmm % 1000000 === 0) return { valeur: divPuissance10(dmm, 7), unite: 'km' };
    if (dmm % 1000 === 0) return { valeur: divPuissance10(dmm, 4), unite: 'm' };
    return null;
};

/**
 * Tirage d'une situation d'échelle : L (dixièmes de cm, [15, 450], jamais 100/200/300/400),
 * E, longueur réelle courte dans une unité usuelle. Repli déterministe : 4,5 cm au 1/2000 → 90 m.
 */
const tirerEchelle = () => {
    for (let essai = 0; essai < 80; essai++) {
        const ech = randomChoice(ECHELLES);
        const L = randInt(15, ech.lMax);
        if (L % 100 === 0) continue;
        const dmm = L * ech.E * 10;       // L/10 cm × E = L×E mm ; ×10 → dixièmes de mm
        const reel = grandeurCourte(dmm);
        if (!reel) continue;
        // Nombre court : au plus quatre chiffres significatifs dans l'unité choisie.
        if (reel.valeur.replace(',', '').replace(/^0+/, '').length > 4) continue;
        return { ech, L, dmm, reel, objet: randomChoice(ech.objets) };
    }
    const ech = ECHELLES[4];
    return { ech, L: 45, dmm: 45 * 2000 * 10, reel: { valeur: '90', unite: 'm' }, objet: ech.objets[0] };
};

const echelleTex = (E) => tex(`\\dfrac{1}{${E}}`);

/** « un sentier » → « ce sentier », « une rue » → « cette rue », « un arbre » → « cet arbre ». */
const demonstratif = (objet) => {
    if (objet.startsWith('une ')) return `cette ${objet.slice(4)}`;
    const nom = objet.replace(/^un /, '');
    return /^[aeiouyéè]/.test(nom) ? `cet ${nom}` : `ce ${nom}`;
};

const consigneUnite = (unites, exemple) => `<p>Écris la réponse avec son unité (${unites}), par exemple : ${exemple}.</p>`;

const genEchelleTrouver = () => {
    const s = tirerEchelle();
    const Lcm = dixiemes(s.L);
    const reelCm = String(s.L * s.ech.E / 10);
    const admises = [
        `1/${s.ech.E}`, `1:${s.ech.E}`,
        `${Lcm}/${reelCm}`, `${s.L}/${s.L * s.ech.E}`,
    ];
    const payload = makeSaisie(
        `1/${s.ech.E}`,
        `<p>Sur ${s.ech.doc}, ${s.objet} de ${s.reel.valeur} ${s.reel.unite} est représenté${s.objet.startsWith('une ') ? 'e' : ''} par un segment de ${Lcm} cm.</p>`
        + `<p>Quelle est l'échelle de ce document ? Écris-la sous la forme d'une fraction, par exemple 1/250.</p>`,
        { ...opts('Trouver l\'échelle d\'un plan'), strict: true, comparaison: 'expression', reponses_acceptees: admises },
    );
    payload._meta.params = {
        niveau: 4, echelle: s.ech.E, plan_cm: Lcm, reel: `${s.reel.valeur} ${s.reel.unite}`, reponse: `1/${s.ech.E}`,
        erreursVisees: ['conversion_oubliee', 'sens_de_l_echelle_inverse'],
    };
    return payload;
};

const genEchelleLongueurReelle = () => {
    const s = tirerEchelle();
    const Lcm = dixiemes(s.L);
    const bonne = `${s.reel.valeur} ${s.reel.unite}`;
    const payload = makeSaisie(
        bonne,
        `<p>Sur ${s.ech.doc} à l'échelle ${echelleTex(s.ech.E)}, ${s.objet} mesure ${Lcm} cm.</p>`
        + `<p>Quelle est la longueur réelle de ${demonstratif(s.objet)} ?</p>`
        + consigneUnite('m ou km', '0,7 km'),
        { ...opts('Calculer une longueur réelle'), strict: true, comparaison: 'expression', reponses_acceptees: ecrituresLongueur(s.dmm) },
    );
    payload._meta.params = {
        niveau: 4, echelle: s.ech.E, plan_cm: Lcm, reponse: bonne,
        erreursVisees: ['conversion_oubliee', 'sens_de_l_echelle_inverse', 'nombre_sans_unite'],
    };
    return payload;
};

const genEchelleLongueurPlan = () => {
    const s = tirerEchelle();
    const Lcm = dixiemes(s.L);
    const bonne = `${Lcm} cm`;
    const payload = makeSaisie(
        bonne,
        `<p>Sur ${s.ech.doc} à l'échelle ${echelleTex(s.ech.E)}, on veut représenter ${s.objet} de ${s.reel.valeur} ${s.reel.unite}.</p>`
        + `<p>Quelle longueur faut-il tracer sur le document ?</p>`
        + consigneUnite('cm', '0,8 cm'),
        { ...opts('Calculer une longueur sur le plan'), strict: true, comparaison: 'expression', reponses_acceptees: ecrituresLongueur(s.L * 10) },
    );
    payload._meta.params = {
        niveau: 4, echelle: s.ech.E, reel: `${s.reel.valeur} ${s.reel.unite}`, reponse: bonne,
        erreursVisees: ['conversion_oubliee', 'sens_de_l_echelle_inverse', 'nombre_sans_unite'],
    };
    return payload;
};

const genEchelleQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const s = tirerEchelle();
        const Lcm = dixiemes(s.L);
        const bonne = `${s.reel.valeur} ${s.reel.unite}`;
        // Conversion oubliée : le nombre de centimètres rendu en mètres.
        const reelCm = String(s.L * s.ech.E / 10);
        // Sens inversé : la longueur du plan divisée par l'échelle, en centimètres.
        // L/10 ÷ E = L / (10 E) ; E = 2^a 5^b → écriture décimale finie.
        const denom = 10 * s.ech.E;
        let k = 0; let n = s.L;
        while ((n % denom) !== 0) { n *= 10; k++; if (k > 12) break; }
        const divise = `${divPuissance10(n / denom, k)} cm`;
        const candidats = [
            { code: 'conversion_oubliee', valeur: `${reelCm} m` },
            { code: 'sens_de_l_echelle_inverse', valeur: divise },
            { code: 'nombre_sans_unite', valeur: s.reel.valeur },
        ];
        const textes = new Set([bonne, ...candidats.map((d) => d.valeur)]);
        if (textes.size < 4) continue;
        // Une même grandeur sous deux écritures serait une seconde bonne réponse : impossible ici
        // (facteur 100 ou 1000 entre « reelCm m » et la bonne réponse, division ≠ multiplication),
        // on le vérifie tout de même.
        if (s.reel.unite === 'm' && reelCm === s.reel.valeur) continue;

        const payload = makeQcm(
            [bonne, ...candidats.map((d) => d.valeur)],
            bonne,
            `<p>Sur ${s.ech.doc} à l'échelle ${echelleTex(s.ech.E)}, ${s.objet} mesure ${Lcm} cm.</p>`
            + `<p>Quelle est la longueur réelle de ${demonstratif(s.objet)} ?</p>`,
            opts('QCM : longueur réelle d\'après un plan'),
        );
        payload._meta.params = {
            niveau: 4, echelle: s.ech.E, plan_cm: Lcm, reponse: bonne,
            distracteurs: candidats,
        };
        return payload;
    }
    // Repli déterministe : 4,5 cm au 1/2000 → 90 m.
    const ech = ECHELLES[4];
    const payload = makeQcm(
        ['90 m', '9000 m', '0,00225 cm', '90'], '90 m',
        `<p>Sur ${ech.doc} à l'échelle ${echelleTex(2000)}, une rue mesure 4,5 cm.</p><p>Quelle est la longueur réelle de cette rue ?</p>`,
        opts('QCM : longueur réelle d\'après un plan'),
    );
    payload._meta.params = {
        niveau: 4, echelle: 2000, plan_cm: '4,5', reponse: '90 m',
        distracteurs: [{ code: 'conversion_oubliee', valeur: '9000 m' }, { code: 'sens_de_l_echelle_inverse', valeur: '0,00225 cm' }, { code: 'nombre_sans_unite', valeur: '90' }],
    };
    return payload;
};

const genEchelleRelier = () => {
    const choisies = shuffle(ECHELLES).slice(0, 4);
    const pairs = choisies.map((ech) => {
        const g = grandeurCourte(ech.E * 100);   // 1 cm × E = E cm = 10 E mm = 100 E dixièmes de mm
        return {
            source: echelleTex(ech.E),
            target: `1 cm sur le plan représente ${g.valeur} ${g.unite} en réalité`,
        };
    });
    const payload = makeRelier(
        pairs,
        '<p>Relie chaque échelle à ce que représente 1 cm sur le plan.</p>',
        opts('Relier une échelle à sa signification'),
    );
    payload._meta.params = { niveau: 4, echelles: choisies.map((e) => e.E) };
    return payload;
};

// ─── Registre ───

const GENERATORS = {
    automatismes_qcm_unique: genAutomatismesQcm,
    automatismes_texte_trou: genAutomatismesTrou,
    automatismes_tableau_vf: genAutomatismesVF,
    pourcentage_nombre_texte: genPourcentageNombre,
    remise_montant_texte: genRemiseMontant,
    remise_prix_texte: genRemisePrix,
    prix_final_texte: genPrixFinalSaisie,
    prix_final_qcm_unique: genPrixFinalQcm,
    remise_tableau_vf: genRemiseVF,
    echelle_trouver_texte: genEchelleTrouver,
    echelle_longueur_reelle_texte: genEchelleLongueurReelle,
    echelle_longueur_plan_texte: genEchelleLongueurPlan,
    echelle_longueur_reelle_qcm_unique: genEchelleQcm,
    echelle_relier: genEchelleRelier,
};

// ─── Catégories ───

export const CATEGORIES = [
    {
        id: 'automatismes',
        label: 'Prendre 1 %, 10 % ou 50 % d\'un nombre',
        formatsList: [
            { key: 'automatismes_qcm_unique', label: 'QCM' },
            { key: 'automatismes_texte_trou', label: 'Trois calculs' },
            { key: 'automatismes_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'pourcentages',
        label: 'Prendre un pourcentage, appliquer une remise ou une augmentation',
        formatsList: [
            { key: 'pourcentage_nombre_texte', label: 'Pourcentage d\'un effectif' },
            { key: 'remise_montant_texte', label: 'Montant de la remise' },
            { key: 'remise_prix_texte', label: 'Prix après remise' },
            { key: 'prix_final_texte', label: 'Prix final (décimal)' },
            { key: 'prix_final_qcm_unique', label: 'QCM' },
            { key: 'remise_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'echelle',
        label: 'Échelle d\'un plan',
        formatsList: [
            { key: 'echelle_trouver_texte', label: 'Trouver l\'échelle' },
            { key: 'echelle_longueur_reelle_texte', label: 'Longueur réelle' },
            { key: 'echelle_longueur_plan_texte', label: 'Longueur sur le plan' },
            { key: 'echelle_longueur_reelle_qcm_unique', label: 'QCM' },
            { key: 'echelle_relier', label: 'Relier' },
        ],
    },
];

// ─── Point d'entrée ───

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
